import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFlagUrl } from '../utils/flagUtils';
import {
  faShip, faAnchor, faLocationDot, faGaugeHigh, faCompass,
  faRulerVertical, faChevronDown, faChevronUp, faBuilding,
  faCalendarAlt, faFlag, faClock, faWater, faIdCard,
  faShieldAlt, faMapPin
} from '@fortawesome/free-solid-svg-icons';
import './VesselTrackingPanel.css';

const NAV_STATUS_COLORS = {
  'Under way using engine': '#28a745',
  'At anchor': '#ffc107',
  'Not under command': '#dc2626',
  'Restricted manoeuvrability': '#fd7e14',
  'Constrained by draught': '#fd7e14',
  'Moored': '#4a6b25',
  'Aground': '#dc2626',
  'Engaged in fishing': '#6f42c1',
  'Under way sailing': '#28a745',
  'AIS-SART / MOB / EPIRB': '#dc2626',
};

const VesselTrackingPanel = ({ position, details, contractEta, resolvedPorts = [] }) => {
  const [specsOpen, setSpecsOpen] = useState(false);

  if (!position && !details) return null;

  const p = position || {};
  const d = details || {};

  const navStatusColor = p.nav_status_color || NAV_STATUS_COLORS[p.nav_status] || '#6c757d';
  const flagCode = p.flag || d.flag || '';
  const flagUrl = getFlagUrl(flagCode);
  const flagDisplayName = d.flag_name || flagCode;

  const formatTimestamp = (ts) => {
    if (!ts) return null;
    try {
      const date = new Date(ts);
      return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
    } catch { return ts; }
  };

  const formatNumber = (val, decimals = 1) => {
    if (val === null || val === undefined) return null;
    return Number(val).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatInt = (val) => {
    if (val === null || val === undefined) return null;
    return Number(val).toLocaleString('en-US');
  };

  const hasStatusData = p.speed != null || p.course != null || p.draught != null;

  return (
    <div className="vtp-container">
      {/* ROW 1: Identity + Current Status side by side */}
      <div className="vtp-row-pair">
        {/* VESSEL IDENTITY */}
        <div className="vtp-card vtp-identity">
          <div className="vtp-identity-header">
            <div className="vtp-identity-left">
              {flagUrl && (
                <img src={flagUrl} alt={flagCode} className="vtp-flag" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div>
                <h3 className="vtp-vessel-name">{p.ship_name || d.ship_name || 'Unknown Vessel'}</h3>
                <span className="vtp-vessel-type">{d.ship_type || p.ship_type || ''}{d.ship_sub_type ? ` -- ${d.ship_sub_type}` : ''}</span>
              </div>
            </div>
            {p.nav_status && (
              <span className="vtp-status-badge" style={{ backgroundColor: navStatusColor }}>
                {p.nav_status_short || p.nav_status}
              </span>
            )}
          </div>
          <div className="vtp-identity-grid">
            <InfoItem icon={faIdCard} label="IMO" value={p.imo || d.imo} />
            <InfoItem icon={faIdCard} label="MMSI" value={p.mmsi || d.mmsi} />
            <InfoItem icon={faShip} label="Call Sign" value={p.call_sign || d.call_sign} />
            <InfoItem icon={faFlag} label="Flag" value={flagDisplayName} />
          </div>
        </div>

        {/* CURRENT STATUS */}
        {hasStatusData && (
          <div className="vtp-card vtp-status">
            <div className="vtp-card-title">
              <FontAwesomeIcon icon={faLocationDot} className="vtp-card-icon" />
              Current Status
            </div>
            <div className="vtp-status-grid">
              <InfoItem icon={faGaugeHigh} label="Speed" value={p.speed != null ? `${formatNumber(p.speed)} kn` : null} />
              <InfoItem icon={faCompass} label="Course" value={p.course != null ? `${formatNumber(p.course)}\u00B0` : null} />
              <InfoItem icon={faRulerVertical} label="Draught" value={p.draught != null ? `${formatNumber(p.draught)} m` : null} />
              <InfoItem icon={faMapPin} label="Heading" value={p.heading != null ? `${formatNumber(p.heading)}\u00B0` : null} />
              <InfoItem icon={faLocationDot} label="Position" value={p.latitude != null && p.longitude != null ? `${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)}` : null} />
            </div>
            {(p.position_updated || p.nav_status_since) && (
              <div className="vtp-timestamp-row">
                {p.position_updated && (
                  <div className="vtp-timestamp">
                    <FontAwesomeIcon icon={faClock} className="vtp-timestamp-icon" />
                    Last updated: {formatTimestamp(p.position_updated)}
                  </div>
                )}
                {p.nav_status_since && (
                  <div className="vtp-timestamp">
                    <FontAwesomeIcon icon={faClock} className="vtp-timestamp-icon" />
                    Status since: {formatTimestamp(p.nav_status_since)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ROW 2: Voyage Info -- full width */}
      {(p.origin || p.destination || p.eta) && (
        <div className="vtp-card vtp-voyage">
          <div className="vtp-card-title">
            <FontAwesomeIcon icon={faAnchor} className="vtp-card-icon" />
            Voyage Info
          </div>
          <div className="vtp-voyage-route">
            {p.origin && (() => {
              const resolved = resolvedPorts.find(rp => rp.type === 'loading');
              const displayName = resolved ? `${resolved.name}, ${resolved.country}` : null;
              return (
                <div className="vtp-voyage-port">
                  <span className="vtp-voyage-label">Origin (AIS Declared)</span>
                  {displayName ? (
                    <span className="vtp-voyage-value">{displayName} <span className="vtp-voyage-code">({p.origin})</span></span>
                  ) : (
                    <span className="vtp-voyage-value">{p.origin}</span>
                  )}
                </div>
              );
            })()}
            {(p.origin && p.destination) && <div className="vtp-voyage-arrow"><FontAwesomeIcon icon={faShip} /></div>}
            {p.destination && (() => {
              const resolved = resolvedPorts.find(rp => rp.type === 'discharge');
              const displayName = resolved ? `${resolved.name}, ${resolved.country}` : null;
              return (
                <div className="vtp-voyage-port">
                  <span className="vtp-voyage-label">Destination (AIS Declared)</span>
                  {displayName ? (
                    <span className="vtp-voyage-value">{displayName} <span className="vtp-voyage-code">({p.destination})</span></span>
                  ) : (
                    <span className="vtp-voyage-value">{p.destination}</span>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="vtp-eta-grid">
            {p.eta && (
              <div className="vtp-eta-item">
                <FontAwesomeIcon icon={faCalendarAlt} className="vtp-eta-icon" />
                <div>
                  <span className="vtp-eta-label">ETA (AIS Declared)</span>
                  <span className="vtp-eta-value">{formatTimestamp(p.eta)}</span>
                </div>
              </div>
            )}
            {contractEta && (
              <div className="vtp-eta-item vtp-eta-contract">
                <FontAwesomeIcon icon={faCalendarAlt} className="vtp-eta-icon" />
                <div>
                  <span className="vtp-eta-label">ETA (Contract)</span>
                  <span className="vtp-eta-value">{contractEta}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ROW 3: Vessel Specifications (Collapsible) -- full width */}
      {d.ship_name && (
        <div className="vtp-card vtp-specs">
          <button type="button" className="vtp-specs-toggle" onClick={() => setSpecsOpen(!specsOpen)}>
            <span className="vtp-card-title" style={{ margin: 0 }}>
              <FontAwesomeIcon icon={faShip} className="vtp-card-icon" />
              Vessel Specifications
            </span>
            <FontAwesomeIcon icon={specsOpen ? faChevronUp : faChevronDown} className="vtp-specs-chevron" />
          </button>
          {specsOpen && (
            <div className="vtp-specs-content">
              <div className="vtp-specs-grid">
                <InfoItem icon={faWater} label="DWT" value={d.deadweight ? `${formatInt(d.deadweight)} MT` : null} />
                <InfoItem icon={faShip} label="Gross Tonnage" value={d.gross_tonnage ? formatInt(d.gross_tonnage) : null} />
                <InfoItem icon={faCalendarAlt} label="Year Built" value={d.year_built} />
                <InfoItem icon={faRulerVertical} label="LOA" value={p.loa ? `${formatNumber(p.loa)} m` : null} />
                <InfoItem icon={faRulerVertical} label="Beam" value={p.beam ? `${formatNumber(p.beam)} m` : null} />
                <InfoItem icon={faShip} label="Status" value={d.operation_status} />
              </div>
              {(d.classification || d.class_status) && (
                <div className="vtp-classification">
                  <div className="vtp-card-subtitle">
                    <FontAwesomeIcon icon={faShieldAlt} className="vtp-card-icon" />
                    Classification
                  </div>
                  <div className="vtp-specs-grid">
                    <InfoItem icon={faShieldAlt} label="Society" value={d.classification} />
                    <InfoItem icon={faShieldAlt} label="Class Status" value={d.class_status} />
                  </div>
                </div>
              )}
              {(d.owner_name || d.manager_name) && (
                <div className="vtp-ownership">
                  {d.owner_name && (
                    <div className="vtp-ownership-item">
                      <FontAwesomeIcon icon={faBuilding} className="vtp-ownership-icon" />
                      <div>
                        <span className="vtp-ownership-label">Owner{d.owner_role ? ` (${d.owner_role})` : ''}</span>
                        <span className="vtp-ownership-value">{d.owner_name}</span>
                        {d.owner_imo && <span className="vtp-ownership-detail">IMO: {d.owner_imo}</span>}
                        {d.owner_address && <span className="vtp-ownership-detail">{d.owner_address}</span>}
                      </div>
                    </div>
                  )}
                  {d.manager_name && (
                    <div className="vtp-ownership-item">
                      <FontAwesomeIcon icon={faBuilding} className="vtp-ownership-icon" />
                      <div>
                        <span className="vtp-ownership-label">Manager{d.manager_role ? ` (${d.manager_role})` : ''}</span>
                        <span className="vtp-ownership-value">{d.manager_name}</span>
                        {d.manager_imo && <span className="vtp-ownership-detail">IMO: {d.manager_imo}</span>}
                        {d.manager_address && <span className="vtp-ownership-detail">{d.manager_address}</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="vtp-info-item">
      <FontAwesomeIcon icon={icon} className="vtp-info-icon" />
      <div className="vtp-info-text">
        <span className="vtp-info-label">{label}</span>
        <span className="vtp-info-value">{value}</span>
      </div>
    </div>
  );
};

export default VesselTrackingPanel;
